import { getEmptyAddress } from "@jti/commerce";
import type { AddressHomeDeliveryFieldValue } from "@jti/commerce-types";
import type { AddressStored } from "@jti/fe-common/types";

import type { FormFieldConfig } from "@checkout-v2/formFields/types/FormFieldConfig.types";
import type { FormFieldFactory } from "@checkout-v2/formFields/types/FormFieldFactory.types";

import { getAddressRegionData } from "./utils";

type AddressAtHomeDeliveryFieldGetDefaultValueArgs = {
  fieldConfig: AddressHomeDeliveryFieldConfig;
};

type AddressAtHomeDeliveryFieldDerivedConfigArgs = {
  countryName: string;
  countryCode: string;
  userProfileAddresses: AddressStored[];
  provinceNameOverride?: string;
};

export type AddressHomeDeliveryFieldConfig = FormFieldConfig<
  "addressHomeDelivery",
  {
    addresses: AddressStored[];
    emptyAddress: AddressStored;
    hasDefaultAddress: boolean;
  }
>;

export const addressHomeDeliveryFieldFactory: FormFieldFactory<
  "addressHomeDelivery",
  AddressHomeDeliveryFieldValue,
  [AddressAtHomeDeliveryFieldDerivedConfigArgs],
  [AddressAtHomeDeliveryFieldGetDefaultValueArgs],
  AddressHomeDeliveryFieldConfig
> = {
  getDerivedConfig: ({
    countryName,
    countryCode,
    userProfileAddresses,
    provinceNameOverride,
  }): AddressHomeDeliveryFieldConfig => {
    const addresses = userProfileAddresses.map((address) => {
      const regionData = getAddressRegionData(countryCode, address);

      return {
        ...address,
        countryName,
        countryCode,
        ...regionData,
        ...(provinceNameOverride && { provinceName: provinceNameOverride }),
      };
    });

    return {
      name: "addressHomeDelivery",
      hasDefaultAddress: addresses.some((address) => address.isDefaultShipping),
      addresses,
      emptyAddress: getEmptyAddress({
        overrides: {
          countryName,
          countryCode,
        },
      }),
    };
  },
  getDefaultValue: ({ fieldConfig }) => {
    if (!fieldConfig.addresses.length) {
      return fieldConfig.emptyAddress;
    }

    const defaultAddress = fieldConfig.addresses.find((address) => address.isDefaultShipping);

    return defaultAddress ?? fieldConfig.emptyAddress;
  },
};
